// src/pages/partner-admin/PartnerCompetitionsPage.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  getPartnerCompetitions,
  getPartnerCompetition, 
  deletePartnerCompetition,
} from "@/services/partnerAdminService";

import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/services/apiService";

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ["draft", "open", "judging", "closed", "archived"];

const STATUS_COLOUR = {
  open:     "#c8f04a",
  draft:    "#555",
  judging:  "#f0c84a",
  closed:   "#888",
  archived: "#333",
};

const EMPTY_FORM = {
  title: "",
  headline: "",
  description: "",
  rules: "",
  prizes: "",
  start_date: "",
  end_date: "",
  allowed_submission_types: "image,video",
  competition_type: "voting",
  status: "draft",
  featured: false,
  visible_on_aggregator: false,
  auto_approve_submissions: false,
};

// ─── API helpers (multipart-aware) ───────────────────────────────────────────
//
// Both functions use the shared `api` axios instance so requests go to the
// correct API host (staging-api.zelospace.com in staging, api.zelospace.com
// in production) rather than the frontend host.

async function saveCompetition(form, imgFile, editingId) {
  const fd = new FormData();

  Object.entries(form).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (typeof v === "object" && !(v instanceof File)) {
      fd.append(k, JSON.stringify(v));
    } else {
      fd.append(k, typeof v === "boolean" ? String(v) : v);
    }
  });

  if (imgFile) fd.append("img", imgFile);

  const url = editingId
    ? `/api/partner-admin/competitions/${editingId}/`
    : "/api/partner-admin/competitions/";
  const method = editingId ? "patch" : "post";

  const res = await api[method](url, fd);
  return res.data;
}

async function changeStatus(id, newStatus) {
  const res = await api.patch(`/api/partner-admin/competitions/${id}/`, {
    status: newStatus,
  });
  return res.data;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CompetitionTable({ competitions, onEdit, onStatusChange, onDelete }) {
  return (
    <div className="pa-table-wrap">
      <table className="pa-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Submissions</th>
            <th>Pending</th>
            <th>Start</th>
            <th>End</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {competitions.map(comp => (
            <tr key={comp.id} onClick={() => onEdit(comp)} className="pa-table__row">
              <td>
                <div className="pa-table__title">{comp.title}</div>
                <div className="pa-table__sub">{comp.headline}</div>
              </td>
              <td onClick={e => e.stopPropagation()}>
                <select
                  className="pa-status-select"
                  value={comp.status}
                  style={{ color: STATUS_COLOUR[comp.status] || "#555" }}
                  onChange={e => onStatusChange(comp, e.target.value)}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              <td className="pa-table__num">{comp.submission_count ?? 0}</td>
              <td className="pa-table__num" style={{ color: comp.pending_count > 0 ? "#f0a84a" : "#555" }}>
                {comp.pending_count ?? 0}
              </td>
              <td className="pa-table__date">{comp.start_date ? new Date(comp.start_date).toLocaleDateString() : "—"}</td>
              <td className="pa-table__date">{comp.end_date   ? new Date(comp.end_date).toLocaleDateString()   : "—"}</td>
              <td onClick={e => e.stopPropagation()}>
                <button
                  className="pa-icon-btn pa-icon-btn--danger"
                  onClick={() => onDelete(comp)}
                  title="Delete"
                >✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ImagePicker({ current, onChange }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const img = preview || current;

  return (
    <div className="pa-img-picker" onClick={() => inputRef.current?.click()}>
      {img ? (
        <img src={img} alt="thumbnail" className="pa-img-picker__preview" />
      ) : (
        <div className="pa-img-picker__placeholder">
          <span className="pa-img-picker__icon">⊕</span>
          <span>Upload thumbnail</span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFile}
      />
    </div>
  );
}

function CompetitionForm({ initial, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState(() => {
    if (!initial) return EMPTY_FORM;
    const normalise = (dt) => dt ? dt.slice(0, 16) : "";
    return {
      ...EMPTY_FORM,
      ...initial,
      start_date: normalise(initial.start_date),
      end_date:   normalise(initial.end_date),
    };
  });
  const [imgFile, setImgFile] = useState(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const isEdit = !!initial?.id;

  const handleSubmit = e => {
    e.preventDefault();
    onSave(form, imgFile);
  };

  return (
    <div className="pa-modal-backdrop" onClick={onCancel}>
      <div className="pa-modal" onClick={e => e.stopPropagation()}>
        <div className="pa-modal__header">
          <h2 className="pa-modal__title">{isEdit ? "Edit Competition" : "New Competition"}</h2>
          <button className="pa-modal__close" onClick={onCancel}>✕</button>
        </div>

        <form className="pa-form" onSubmit={handleSubmit}>
          {error && <div className="pa-form__error">{error}</div>}

          {/* Thumbnail */}
          <div className="pa-form__field">
            <label>Thumbnail</label>
            <ImagePicker
              current={initial?.img_url || null}
              onChange={setImgFile}
            />
          </div>

          <div className="pa-form__row">
            <div className="pa-form__field pa-form__field--grow">
              <label>Title *</label>
              <input value={form.title} onChange={e => set("title", e.target.value)} required />
            </div>
            <div className="pa-form__field">
              <label>Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="pa-form__field">
            <label>Headline *</label>
            <input value={form.headline} onChange={e => set("headline", e.target.value)} required />
          </div>

          <div className="pa-form__field">
            <label>Description *</label>
            <textarea rows={4} value={form.description} onChange={e => set("description", e.target.value)} required />
          </div>

          <div className="pa-form__row">
            <div className="pa-form__field pa-form__field--grow">
              <label>Rules *</label>
              <textarea rows={3} value={form.rules} onChange={e => set("rules", e.target.value)} required />
            </div>
            <div className="pa-form__field pa-form__field--grow">
              <label>Prizes *</label>
              <textarea rows={3} value={form.prizes} onChange={e => set("prizes", e.target.value)} required />
            </div>
          </div>

          <div className="pa-form__row">
            <div className="pa-form__field pa-form__field--grow">
              <label>Start Date *</label>
              <input type="datetime-local" value={form.start_date} onChange={e => set("start_date", e.target.value)} required />
            </div>
            <div className="pa-form__field pa-form__field--grow">
              <label>End Date *</label>
              <input type="datetime-local" value={form.end_date} onChange={e => set("end_date", e.target.value)} required />
            </div>
          </div>

          <div className="pa-form__row">
            <div className="pa-form__field pa-form__field--grow">
              <label>Allowed Submission Types</label>
              <input value={form.allowed_submission_types} onChange={e => set("allowed_submission_types", e.target.value)} placeholder="image,video" />
            </div>
            <div className="pa-form__field pa-form__field--grow">
              <label>Competition Type</label>
              <select value={form.competition_type || ""} onChange={e => set("competition_type", e.target.value)} disabled={isEdit}>
                <option value="voting">Voting</option>
                <option value="community_score">Community Score</option>
                <option value="judge_scored" disabled>Judge Scored (coming soon)</option>
                <option value="bracket" disabled>Bracket (coming soon)</option>
                <option value="mastery" disabled>Mastery (coming soon)</option>
              </select>
            </div>
          </div>

          <div className="pa-form__checks">
            {[
              ["featured",                 "Featured on homepage"],
              ["visible_on_aggregator",    "Visible on Zelospace"],
              ["auto_approve_submissions", "Auto-approve submissions"],
            ].map(([key, label]) => (
              <label key={key} className="pa-check">
                <input
                  type="checkbox"
                  checked={!!form[key]}
                  onChange={e => set(key, e.target.checked)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <div className="pa-modal__footer">
            <button type="button" className="pa-btn pa-btn--ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" className="pa-btn pa-btn--primary" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Competition"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PartnerCompetitionsPage() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [filter, setFilter]       = useState("all");
  const [search, setSearch]       = useState("");
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState(null);

  const location = useLocation();

useEffect(() => {
  if (location.state?.editId) {
    getPartnerCompetition(location.state.editId)
      .then(full => {
        setEditing(full);
        setShowForm(true);
      })
      .catch(() => {});
    // clear the state so refreshing doesn't re-open it
    window.history.replaceState({}, "");
  }
}, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== "all") params.status = filter;
      if (search) params.search = search;
      const data = await getPartnerCompetitions(params);
      setCompetitions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form, imgFile) => {
    setSaving(true);
    setFormError(null);
    try {
      await saveCompetition(form, imgFile, editing?.id);
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (comp, newStatus) => {
    try {
      await changeStatus(comp.id, newStatus);
      setCompetitions(cs => cs.map(c => c.id === comp.id ? { ...c, status: newStatus } : c));
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (comp) => {
    if (!confirm(`Delete "${comp.title}"? This cannot be undone.`)) return;
    try {
      await deletePartnerCompetition(comp.id);
      setCompetitions(cs => cs.filter(c => c.id !== comp.id));
    } catch (err) {
      alert(err.message);
    }
  };

const handleEdit = async (comp) => {
  setFormError(null);
  try {
    const full = await getPartnerCompetition(comp.id);
    setEditing(full);
    setShowForm(true);
  } catch (err) {
    alert("Failed to load competition details.");
  }
};

  const filtered = competitions.filter(c => {
    if (filter !== "all" && c.status !== filter) return false;
    if (search && !c.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="pa-competitions">
      {/* Toolbar */}
      <div className="pa-toolbar">
        <div className="pa-toolbar__left">
          <input
            className="pa-search"
            placeholder="Search competitions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="pa-filter-tabs">
            {["all", ...STATUS_OPTIONS].map(s => (
              <button
                key={s}
                className={"pa-filter-tab" + (filter === s ? " pa-filter-tab--active" : "")}
                onClick={() => setFilter(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        <button
          className="pa-btn pa-btn--primary"
          onClick={() => { setEditing(null); setShowForm(true); setFormError(null); }}
        >
          + New Competition
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="pa-page-loading">Loading...</div>
      ) : error ? (
        <div className="pa-page-error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="pa-empty">
          <span>{search || filter !== "all" ? "No competitions match your filters." : "No competitions yet."}</span>
        </div>
      ) : (
        <CompetitionTable
          competitions={filtered}
          onEdit={handleEdit}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
        />
      )}

      {/* Form modal */}
      {showForm && (
        <CompetitionForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          saving={saving}
          error={formError}
        />
      )}

      <style>{`
        .pa-competitions { display: flex; flex-direction: column; gap: 20px; }

        .pa-toolbar {
          display: flex; align-items: center;
          justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .pa-toolbar__left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pa-search {
          background: #111; border: 1px solid #222; color: #ccc;
          padding: 8px 12px; border-radius: 6px; font-size: 13px;
          width: 220px; outline: none;
        }
        .pa-search:focus { border-color: #333; }
        .pa-filter-tabs { display: flex; gap: 4px; }
        .pa-filter-tab {
          background: none; border: 1px solid #1e1e1e; color: #555;
          font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
          padding: 5px 10px; border-radius: 4px; cursor: pointer;
          transition: border-color 0.15s, color 0.15s;
        }
        .pa-filter-tab:hover { border-color: #333; color: #aaa; }
        .pa-filter-tab--active {
          border-color: var(--colour-primary, #c8f04a);
          color: var(--colour-primary, #c8f04a);
        }

        .pa-table-wrap {
          background: #111; border: 1px solid #1e1e1e;
          border-radius: 8px; overflow: hidden;
        }
        .pa-table { width: 100%; border-collapse: collapse; }
        .pa-table thead tr { border-bottom: 1px solid #1e1e1e; }
        .pa-table th {
          text-align: left; padding: 10px 16px;
          font-size: 10px; color: #444; text-transform: uppercase;
          letter-spacing: 0.07em; font-weight: 600;
        }
        .pa-table__row {
          border-bottom: 1px solid #161616;
          cursor: pointer; transition: background 0.1s;
        }
        .pa-table__row:last-child { border-bottom: none; }
        .pa-table__row:hover { background: #161616; }
        .pa-table td { padding: 12px 16px; vertical-align: middle; }
        .pa-table__title { font-size: 13px; color: #ccc; font-weight: 500; }
        .pa-table__sub   { font-size: 11px; color: #444; margin-top: 2px; }
        .pa-table__num   { font-size: 13px; color: #666; text-align: center; }
        .pa-table__date  { font-size: 11px; color: #555; white-space: nowrap; }

        .pa-status-select {
          background: #1a1a1a; border: 1px solid #2a2a2a;
          border-radius: 4px; padding: 4px 8px; font-size: 11px;
          text-transform: uppercase; letter-spacing: 0.06em;
          font-weight: 600; cursor: pointer; outline: none;
        }
        .pa-icon-btn {
          background: none; border: none; cursor: pointer;
          font-size: 12px; padding: 4px 8px; border-radius: 4px;
          transition: background 0.1s;
        }
        .pa-icon-btn--danger { color: #555; }
        .pa-icon-btn--danger:hover { background: #2a1111; color: #f04a4a; }

        /* Image picker */
        .pa-img-picker {
          width: 100%; height: 140px;
          background: #1a1a1a; border: 1px dashed #2a2a2a;
          border-radius: 6px; cursor: pointer; overflow: hidden;
          display: flex; align-items: center; justify-content: center;
          transition: border-color 0.15s;
        }
        .pa-img-picker:hover { border-color: var(--colour-primary, #c8f04a); }
        .pa-img-picker__preview {
          width: 100%; height: 100%; object-fit: cover;
        }
        .pa-img-picker__placeholder {
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          color: #444; font-size: 12px;
        }
        .pa-img-picker__icon { font-size: 22px; color: #333; }

        /* Modal */
        .pa-modal-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.75);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 20px;
        }
        .pa-modal {
          background: #141414; border: 1px solid #222; border-radius: 10px;
          width: 100%; max-width: 680px; max-height: 90vh;
          overflow-y: auto; display: flex; flex-direction: column;
        }
        .pa-modal__header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 20px 24px 16px; border-bottom: 1px solid #1e1e1e;
          position: sticky; top: 0; background: #141414; z-index: 1;
        }
        .pa-modal__title { font-size: 15px; font-weight: 600; color: #e8e8e8; margin: 0; }
        .pa-modal__close {
          background: none; border: none; color: #555; font-size: 14px;
          cursor: pointer; padding: 4px 8px;
        }
        .pa-modal__close:hover { color: #aaa; }
        .pa-modal__footer {
          display: flex; justify-content: flex-end; gap: 10px;
          padding: 16px 24px 20px; border-top: 1px solid #1e1e1e;
          position: sticky; bottom: 0; background: #141414;
        }

        .pa-form { padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
        .pa-form__error {
          background: #2a1111; border: 1px solid #4a2222;
          color: #f08888; padding: 10px 14px; border-radius: 6px; font-size: 12px;
        }
        .pa-form__row { display: flex; gap: 12px; }
        .pa-form__field { display: flex; flex-direction: column; gap: 5px; }
        .pa-form__field--grow { flex: 1; }
        .pa-form__field label {
          font-size: 11px; color: #555;
          text-transform: uppercase; letter-spacing: 0.06em;
        }
        .pa-form__field input,
        .pa-form__field select,
        .pa-form__field textarea {
          background: #1a1a1a; border: 1px solid #2a2a2a; color: #ccc;
          padding: 8px 11px; border-radius: 6px; font-size: 13px; outline: none;
          transition: border-color 0.15s; font-family: inherit; resize: vertical;
        }
        .pa-form__field input:focus,
        .pa-form__field select:focus,
        .pa-form__field textarea:focus {
          border-color: var(--colour-primary, #c8f04a);
        }
        .pa-form__checks { display: flex; gap: 16px; flex-wrap: wrap; }
        .pa-check {
          display: flex; align-items: center; gap: 7px;
          font-size: 12px; color: #888; cursor: pointer; user-select: none;
        }
        .pa-check input[type=checkbox] { accent-color: var(--colour-primary, #c8f04a); }

        .pa-btn {
          padding: 9px 18px; border-radius: 6px; font-size: 13px;
          cursor: pointer; border: none; font-weight: 500; transition: opacity 0.15s;
        }
        .pa-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .pa-btn--primary { background: var(--colour-primary, #c8f04a); color: #0d0d0d; }
        .pa-btn--primary:hover:not(:disabled) { opacity: 0.9; }
        .pa-btn--ghost {
          background: none; border: 1px solid #2a2a2a; color: #888;
        }
        .pa-btn--ghost:hover { border-color: #444; color: #ccc; }

        .pa-empty {
          background: #111; border: 1px dashed #222; border-radius: 8px;
          padding: 40px; display: flex; align-items: center; justify-content: center;
          color: #444; font-size: 13px;
        }
        .pa-page-loading { color: #555; font-size: 13px; padding: 40px 0; }
        .pa-page-error   { color: #f04a4a; font-size: 13px; padding: 40px 0; }
      `}</style>
    </div>
  );
}